<template>
	<Popover
		@hide="onPopoverHide"
		@show="onPopoverShow"
		ref="popoverRef"
		v-if="useDesktopPopover"
	>
		<div class="colorPickerSheet__content colorPickerSheet__content--popover">
			<ColorPickerSheetBody
				:label="label"
				:model-value="internalValue"
				:swatch-colors="swatchColors"
				@update:model-value="onInternalChange"
			/>
		</div>
	</Popover>

	<BottomSheet
		:title="label ?? undefined"
		:visible="drawerVisible"
		@update:visible="onDrawerVisibleUpdate"
		v-else
	>
		<div class="colorPickerSheet__content colorPickerSheet__content--drawer">
			<ColorPickerSheetBody
				:label="null"
				:model-value="internalValue"
				:swatch-colors="swatchColors"
				@update:model-value="onInternalChange"
			/>

			<div class="colorPickerSheet__footer">
				<PrimeButton
					:label="$t('common_ok')"
					@click="closeDrawer"
					fluid
				/>
			</div>
		</div>
	</BottomSheet>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, ref, watch } from 'vue';

import PrimeButton from 'primevue/button';
import Popover from 'primevue/popover';
import { useBreakpoint } from 'src/composables/useBreakpoint';

import BottomSheet from 'src/components/common/BottomSheet.vue';
import ColorPickerSheetBody from 'src/components/common/ColorPickerSheetBody.vue';

export default defineComponent({
	components: { BottomSheet, ColorPickerSheetBody, Popover, PrimeButton },
	emits: ['commit', 'update:modelValue'],
	name: 'ColorPickerSheet',
	props: {
		label: { default: null, required: false, type: String },
		modelValue: { required: true, type: String },
		swatchColors: {
			default: () => [
				'#ef4444',
				'#f97316',
				'#f59e0b',
				'#eab308',
				'#84cc16',
				'#22c55e',
				'#10b981',
				'#14b8a6',
				'#06b6d4',
				'#0ea5e9',
				'#3b82f6',
				'#6366f1',
				'#8b5cf6',
				'#a855f7',
				'#d946ef',
				'#ec4899',
				'#f43f5e',
				'#64748b',
				'#6b7280',
				'#94a3b8',
				'#a8a29e',
				'#000000',
				'#ffffff',
			],
			required: false,
			type: Array as PropType<string[]>,
		},
	},
	setup(props, { emit, expose }) {
		const { isDesktop } = useBreakpoint();

		const popoverRef = ref<InstanceType<typeof Popover> | null>(null);
		const drawerVisible = ref(false);
		const internalValue = ref(props.modelValue);

		const useDesktopPopover = computed((): boolean => isDesktop.value);

		watch(
			() => props.modelValue,
			(next) => {
				internalValue.value = next;
			}
		);

		function onInternalChange(next: string): void {
			internalValue.value = next;
			emit('update:modelValue', next);
		}

		function onPopoverShow(): void {
			internalValue.value = props.modelValue;
		}

		function onPopoverHide(): void {
			emit('commit', internalValue.value);
		}

		function onDrawerVisibleUpdate(next: boolean): void {
			const wasOpen = drawerVisible.value;
			drawerVisible.value = next;

			if (wasOpen && !next) {
				emit('commit', internalValue.value);
			}
		}

		function closeDrawer(): void {
			drawerVisible.value = false;
		}

		function open(event?: Event): void {
			if (useDesktopPopover.value) {
				popoverRef.value?.show(event as Event);
				return;
			}

			internalValue.value = props.modelValue;
			drawerVisible.value = true;
		}

		expose({ open });

		return {
			closeDrawer,
			drawerVisible,
			internalValue,
			onDrawerVisibleUpdate,
			onInternalChange,
			onPopoverHide,
			onPopoverShow,
			popoverRef,
			useDesktopPopover,
		};
	},
});
</script>

<style lang="scss" scoped>
.colorPickerSheet__content--popover {
	padding: 16px;
	width: 340px;
}

.colorPickerSheet__content--drawer {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-height: 0;
	padding: 4px 8px 8px;
}

.colorPickerSheet__footer {
	padding-top: 8px;
}
</style>
